import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LogOut, TrendingUp, Wallet, DollarSign, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Upload, Calendar, Bell } from 'lucide-react';

const API = '/api';

export default function PartnerDashboard({ partnerId, onLogout }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [partnerData, setPartnerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [depositRequests, setDepositRequests] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [settings, setSettings] = useState({});
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Deposit form state
  const [depMethod, setDepMethod] = useState('instapay');

  // Withdrawal form state
  const [wdAmount, setWdAmount] = useState('');
  const [wdMethod, setWdMethod] = useState('instapay');
  const [wdAccountNumber, setWdAccountNumber] = useState('');

  // Notifications
  const [notifications, setNotifications] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [partnerRes, maintenanceRes, settingsRes, depRes, withRes, notifRes] = await Promise.all([
        axios.get(`${API}/partners/${partnerId}`, { headers }),
        axios.get(`${API}/system/maintenance-mode`),
        axios.get(`${API}/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/partners/${partnerId}/deposit-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partners/${partnerId}/withdrawal-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications/partner/${partnerId}`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPartnerData(partnerRes.data);
      setMaintenanceMode(maintenanceRes.data.enabled || false);
      setSettings(settingsRes.data || {});
      setDepositRequests(depRes.data || []);
      setWithdrawalRequests(withRes.data || []);
      setNotifications(notifRes.data || []);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 10 seconds to reflect admin changes
  useEffect(() => {
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleDepositRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = new FormData(e.target);
      await axios.post(`${API}/partners/${partnerId}/deposit-requests`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('تم إرسال طلب الإيداع');
      setDepositOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawRequest = async (e) => {
    e.preventDefault();
    const amount = parseFloat(wdAmount);
    const availBal = partnerData?.available_balance || 0;
    if (amount > availBal) {
      toast.error(`المبلغ المطلوب أكبر من الرصيد المتاح (${availBal.toLocaleString()} ج.م)`);
      return;
    }
    if (!wdAccountNumber.trim()) {
      toast.error('يرجى إدخال رقم الحساب أو المحفظة');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('amount', amount);
      form.append('payment_method', wdMethod);
      form.append('account_number', wdAccountNumber);
      form.append('notes', '');
      await axios.post(`${API}/partners/${partnerId}/withdrawal-requests`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('تم إرسال طلب السحب');
      setWithdrawOpen(false);
      setWdAmount(''); setWdMethod('instapay'); setWdAccountNumber('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      rejected: 'bg-red-100 text-red-700 border-red-300'
    };
    const labels = { pending: 'قيد المراجعة', approved: 'مقبول', rejected: 'مرفوض' };
    return <Badge className={`${colors[status] || colors.pending} border`}>{labels[status] || status}</Badge>;
  };

  const paymentAccounts = settings.payment_accounts ? JSON.parse(settings.payment_accounts) : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-500">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!partnerData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" data-testid="partner-dashboard">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-black text-emerald-700">افيوس جروب</h1>
              <p className="text-sm text-slate-500 mt-1">مرحباً، {partnerData.full_name}</p>
            </div>
            <Button data-testid="logout-button" onClick={onLogout} variant="outline" className="rounded-full border-slate-300">
              <LogOut className="ml-2 h-4 w-4" />
              خروج
            </Button>
          </div>
        </div>
      </header>

      {maintenanceMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">النظام تحت الصيانة حالياً</span>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards - Balance + Last Month Profit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-700 to-slate-900 text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm font-medium">الرصيد</p>
                  <p className="text-3xl font-black mt-1" data-testid="total-balance">{(partnerData.available_balance || 0).toLocaleString()}</p>
                  <p className="text-slate-400 text-xs mt-1">ج.م</p>
                </div>
                <DollarSign className="h-12 w-12 text-slate-400/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm font-medium">ربح الشهر السابق</p>
                  <p className="text-3xl font-black mt-1" data-testid="last-month-profit">{(partnerData.last_month_profit || 0).toLocaleString()}</p>
                  <p className="text-amber-200 text-xs mt-1">ج.م</p>
                </div>
                <TrendingUp className="h-12 w-12 text-amber-200/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profits" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-full w-full justify-start gap-1 overflow-x-auto">
            <TabsTrigger value="profits" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-sm font-bold px-5">
              سجل الأرباح
            </TabsTrigger>
            <TabsTrigger value="deposits" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-sm font-bold px-5">
              الإيداعات
            </TabsTrigger>
            <TabsTrigger value="withdrawals" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-sm font-bold px-5">
              السحوبات
            </TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-sm font-bold px-5" data-testid="partner-notif-tab">
              الإشعارات{notifications.filter(n => !n.read).length > 0 && <Badge className="bg-red-500 text-white mr-1 text-xs px-1.5">{notifications.filter(n => !n.read).length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Profits Tab */}
          <TabsContent value="profits">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="text-lg font-bold">سجل الأرباح الشهرية</CardTitle>
              </CardHeader>
              <CardContent>
                {(!partnerData.transactions || partnerData.transactions.length === 0) ? (
                  <div className="text-center py-12 text-slate-400">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>لا توجد أرباح موزعة حتى الآن</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right font-bold">الوصف</TableHead>
                          <TableHead className="text-right font-bold">المبلغ</TableHead>
                          <TableHead className="text-right font-bold">التاريخ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {partnerData.transactions.map((t) => (
                          <TableRow key={t.id}>
                            <TableCell>{t.description}</TableCell>
                            <TableCell className="font-bold text-emerald-600">{(t.amount || 0).toLocaleString()} ج.م</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(t.date).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deposits Tab */}
          <TabsContent value="deposits">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-bold">طلبات الإيداع</CardTitle>
                  <CardDescription>إيداع مبالغ في حسابك الاستثماري</CardDescription>
                </div>
                <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="new-deposit-btn">
                      <ArrowDownCircle className="ml-2 h-4 w-4" />
                      طلب إيداع
                    </Button>
                  </DialogTrigger>
                  <DialogContent dir="rtl" className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>طلب إيداع جديد</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleDepositRequest} className="space-y-4">
                      <div>
                        <Label>المبلغ (ج.م)</Label>
                        <Input name="amount" type="number" step="0.01" required className="mt-1" placeholder="أدخل المبلغ" data-testid="deposit-amount" />
                      </div>
                      <div>
                        <Label>طريقة الدفع</Label>
                        <select name="payment_method" value={depMethod} onChange={e => setDepMethod(e.target.value)} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="deposit-method">
                          <option value="instapay">انستاباي</option>
                          <option value="vodafone_cash">فودافون كاش</option>
                          <option value="bank_transfer">تحويل بنكي</option>
                          <option value="cash">نقدي</option>
                        </select>
                      </div>
                      {/* Show admin's accounts for the selected method */}
                      {(() => {
                        const filtered = paymentAccounts.filter(a => a.method === depMethod);
                        return filtered.length > 0 ? (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="deposit-admin-accounts">
                            <p className="font-bold text-emerald-800 text-sm mb-2">قم بالتحويل إلى:</p>
                            {filtered.map((acc, i) => (
                              <div key={i} className="flex justify-between text-sm py-2 border-b border-emerald-100 last:border-0">
                                <span className="font-medium text-emerald-700">{acc.name}</span>
                                <span className="font-mono font-bold text-emerald-900 select-all">{acc.number}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                            لا توجد أرقام تحويل لهذه الطريقة، تواصل مع الإدارة
                          </div>
                        );
                      })()}
                      <div>
                        <Label>صورة إيصال التحويل</Label>
                        <Input name="image" type="file" accept="image/*" required className="mt-1" data-testid="deposit-image" />
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-deposit">
                        {submitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {depositRequests.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <ArrowDownCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>لا توجد طلبات إيداع</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right font-bold">المبلغ</TableHead>
                          <TableHead className="text-right font-bold">الطريقة</TableHead>
                          <TableHead className="text-right font-bold">الحالة</TableHead>
                          <TableHead className="text-right font-bold">التاريخ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {depositRequests.map(r => (
                          <TableRow key={r.id} data-testid="deposit-row">
                            <TableCell className="font-bold">{r.requested_amount?.toLocaleString()} ج.م</TableCell>
                            <TableCell>{methodLabel(r.payment_method)}</TableCell>
                            <TableCell>{statusBadge(r.status)}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(r.created_at).toLocaleDateString('ar-EG')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-bold">طلبات السحب</CardTitle>
                  <CardDescription>سحب مبالغ من حسابك</CardDescription>
                </div>
                <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="new-withdrawal-btn">
                      <ArrowUpCircle className="ml-2 h-4 w-4" />
                      طلب سحب
                    </Button>
                  </DialogTrigger>
                  <DialogContent dir="rtl" className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>طلب سحب جديد</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleWithdrawRequest} className="space-y-4">
                      {/* Available balance */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center" data-testid="available-balance-info">
                        <p className="text-sm text-blue-600 mb-1">الرصيد المتاح للسحب</p>
                        <p className="text-2xl font-black text-blue-800">{(partnerData?.available_balance || 0).toLocaleString()} ج.م</p>
                        <p className="text-xs text-blue-500 mt-1">رأس المال + الأرباح التراكمية</p>
                      </div>
                      <div>
                        <Label>المبلغ (ج.م)</Label>
                        <Input type="number" step="0.01" required className="mt-1" placeholder="أدخل المبلغ"
                          value={wdAmount} onChange={e => setWdAmount(e.target.value)}
                          max={partnerData?.available_balance || 0}
                          data-testid="withdraw-amount" />
                        {wdAmount && parseFloat(wdAmount) > (partnerData?.available_balance || 0) && (
                          <p className="text-red-500 text-xs mt-1 font-bold">المبلغ أكبر من الرصيد المتاح</p>
                        )}
                      </div>
                      <div>
                        <Label>طريقة الاستلام</Label>
                        <select value={wdMethod} onChange={e => setWdMethod(e.target.value)} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="withdraw-method">
                          <option value="instapay">انستاباي</option>
                          <option value="vodafone_cash">فودافون كاش</option>
                          <option value="bank_transfer">تحويل بنكي</option>
                          <option value="cash">نقدي</option>
                        </select>
                      </div>
                      <div>
                        <Label>{wdMethod === 'bank_transfer' ? 'رقم الحساب البنكي' : wdMethod === 'instapay' ? 'رقم انستاباي' : wdMethod === 'vodafone_cash' ? 'رقم فودافون كاش' : 'رقم المحفظة / الحساب'}</Label>
                        <Input type="text" required className="mt-1" placeholder="أدخل الرقم الخاص بك للتحويل عليه"
                          value={wdAccountNumber} onChange={e => setWdAccountNumber(e.target.value)}
                          data-testid="withdraw-account-number" />
                      </div>
                      <Button type="submit" disabled={submitting || !wdAmount || parseFloat(wdAmount) <= 0 || parseFloat(wdAmount) > (partnerData?.available_balance || 0) || !wdAccountNumber.trim()}
                        className="w-full rounded-full bg-blue-600 hover:bg-blue-700" data-testid="submit-withdraw">
                        {submitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {withdrawalRequests.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <ArrowUpCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>لا توجد طلبات سحب</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right font-bold">المبلغ</TableHead>
                          <TableHead className="text-right font-bold">الطريقة</TableHead>
                          <TableHead className="text-right font-bold">رقم الحساب</TableHead>
                          <TableHead className="text-right font-bold">الحالة</TableHead>
                          <TableHead className="text-right font-bold">التاريخ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {withdrawalRequests.map(r => (
                          <TableRow key={r.id} data-testid="withdrawal-row">
                            <TableCell className="font-bold">{r.requested_amount?.toLocaleString()} ج.م</TableCell>
                            <TableCell>{methodLabel(r.payment_method)}</TableCell>
                            <TableCell className="font-mono text-sm">{r.account_number || '-'}</TableCell>
                            <TableCell>{statusBadge(r.status)}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(r.created_at).toLocaleDateString('ar-EG')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><Bell className="h-5 w-5" />الإشعارات</CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? <p className="text-center py-8 text-slate-400">لا توجد إشعارات</p> : (
                  <div className="space-y-3">
                    {notifications.map(n => (
                      <div key={n.id} className={`p-4 rounded-xl border ${n.read ? 'bg-white border-slate-200' : 'bg-blue-50 border-blue-200'}`}
                        onClick={async () => { if (!n.read) { try { await axios.patch(`${API}/notifications/${n.id}/read`, {}, { headers }); fetchData(); } catch {} } }}
                        data-testid={`notif-${n.id}`}>
                        <div className="flex justify-between items-start">
                          <p className={`text-sm ${n.read ? 'text-slate-600' : 'text-blue-800 font-bold'}`}>{n.message}</p>
                          {!n.read && <Badge className="bg-blue-500 text-white text-xs">جديد</Badge>}
                        </div>
                        <p className="text-xs text-slate-400 mt-2">{new Date(n.created_at).toLocaleDateString('ar-EG')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function methodLabel(method) {
  const labels = {
    instapay: 'انستاباي',
    vodafone_cash: 'فودافون كاش',
    bank_transfer: 'تحويل بنكي',
    cash: 'نقدي'
  };
  return labels[method] || method;
}
